import axios, {
  AxiosHeaders,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { signOut } from 'firebase/auth';

import { auth } from '../firebaseAuth';
import { resolveFrontendApiBase } from './apiBase';
import { getBrowserVerificationToken } from './appCheck';
import { getAuthHeaders } from './authHeaders';
import { AUTH_STAGE_TIMEOUT_MS, runAuthStage } from './authTelemetry';
import {
  API_SESSION_EXPIRED_EVENT,
  createApiAuthResponseErrorHandler,
  isApiRequestUrl,
  type AuthRetryConfig,
} from './apiAuthRecovery';

export { API_SESSION_EXPIRED_EVENT } from './apiAuthRecovery';

let installed = false;

export const installApiAuthInterceptor = (): void => {
  if (installed) return;
  installed = true;
  const apiBase = resolveFrontendApiBase();

  axios.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    const requestUrl = new URL(axios.getUri(config), typeof window !== 'undefined' ? window.location.origin : apiBase).href;
    if (!isApiRequestUrl(requestUrl, apiBase)) {
      return config;
    }
    const headers = AxiosHeaders.from(config.headers);
    const currentUser = auth.currentUser;
    if (currentUser && !headers.has('Authorization')) {
      Object.entries(await getAuthHeaders(currentUser)).forEach(([key, value]) => headers.set(key, value));
    } else if (!headers.has('X-Firebase-AppCheck')) {
      const browserToken = await getBrowserVerificationToken();
      if (browserToken) headers.set('X-Firebase-AppCheck', browserToken);
    }
    config.headers = headers;
    return config;
  });

  // fetch consumers share the same origin gate and initial authentication as
  // Axios. This wrapper never retries a mutation or sends tokens to asset hosts.
  if (typeof window !== 'undefined' && typeof window.fetch === 'function') {
    const originalFetch = window.fetch.bind(window);
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      const target = input instanceof Request ? input.url : String(input);
      const absolute = new URL(target, window.location.href).href;
      if (!isApiRequestUrl(absolute, apiBase)) return originalFetch(input, init);
      const headers = new Headers(init?.headers || (input instanceof Request ? input.headers : undefined));
      const user = auth.currentUser;
      if (user && !headers.has('Authorization')) {
        Object.entries(await getAuthHeaders(user)).forEach(([key, value]) => headers.set(key, value));
      } else if (!headers.has('X-Firebase-AppCheck')) {
        const browserToken = await getBrowserVerificationToken();
        if (browserToken) headers.set('X-Firebase-AppCheck', browserToken);
      }
      return originalFetch(input, { ...init, headers });
    };
  }

  const responseErrorHandler = createApiAuthResponseErrorHandler({
    apiBase,
    getCurrentUser: () => auth.currentUser,
    replayRequest: config => axios.request(config as AxiosRequestConfig),
    setAuthorizationHeader: (config: AuthRetryConfig, token: string) => {
      const headers = AxiosHeaders.from(config.headers || {});
      headers.set('Authorization', `Bearer ${token}`);
      config.headers = headers;
    },
    terminateSession: async () => {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(API_SESSION_EXPIRED_EVENT));
      }
      await runAuthStage(
        'session_sign_out',
        () => signOut(auth),
        { timeoutMs: AUTH_STAGE_TIMEOUT_MS.signOut },
      );
    },
  });

  axios.interceptors.response.use(
    response => response,
    responseErrorHandler,
  );
};
